import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { Field, Form, Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToastHook } from '../../appToast';
import Loader from '../../appLoader';
import {
  QUERY_KEYS,
  useIssueStatuses,
  useMetricStatusExclusion,
  useMetricStatusExclusionMutation,
} from '../api';
import { METRIC_STATUS_EXCLUSION_METRICS } from '../helpers/bottomView.types';
import { SaveAndPreviousButton } from './SaveAndPreviousButton';

interface MetricStatusExclusionProps {
  integrationId: string;
  isManager?: boolean;
}

interface MetricStatusExclusionFormValues {
  exclusions: Record<string, string[]>;
}

const buildInitialExclusions = (
  savedExclusions: Record<string, string[]> = {}
): Record<string, string[]> =>
  METRIC_STATUS_EXCLUSION_METRICS.reduce(
    (acc, { key }) => ({
      ...acc,
      [key]: savedExclusions[key] ?? [],
    }),
    {}
  );

const MetricStatusExclusion = ({
  integrationId,
  isManager = false,
}: MetricStatusExclusionProps) => {
  const [newToast] = useToastHook();
  const queryClient = useQueryClient();
  const [initialValues, setInitialValues] =
    useState<MetricStatusExclusionFormValues>({
      exclusions: buildInitialExclusions(),
    });

  const { data: exclusionData, isLoading: exclusionLoading } =
    useMetricStatusExclusion(integrationId);
  const { data: issueStatuses, isLoading: issueStatusLoading } =
    useIssueStatuses(integrationId);
  const {
    mutate: saveMetricStatusExclusion,
    isLoading: saveMetricStatusExclusionLoading,
  } = useMetricStatusExclusionMutation();

  const statusOptions = useMemo(
    () =>
      (issueStatuses?.dto ?? []).map((status: string) => ({
        value: status,
        label: status,
      })),
    [issueStatuses?.dto]
  );

  useEffect(() => {
    setInitialValues({
      exclusions: buildInitialExclusions(exclusionData?.dto?.exclusions ?? {}),
    });
  }, [exclusionData?.dto?.exclusions]);

  const onSubmit = (values: MetricStatusExclusionFormValues) => {
    saveMetricStatusExclusion(
      { exclusions: values.exclusions, integrationId },
      {
        onSuccess: () => {
          newToast({
            message: 'Status exclusion map saved successfully!',
            status: 'success',
          });
          queryClient.invalidateQueries([
            QUERY_KEYS.metricStatusExclusion,
            integrationId,
          ]);
        },
        onError: (err: any) => {
          newToast({
            message: err?.message ?? 'Something went wrong!',
            status: 'error',
          });
        },
      }
    );
  };

  return (
    <>
      <Text
        fontSize="sm"
        fontFamily="heading"
        fontWeight="semibold"
        color="text.primary"
        lineHeight="17px"
        mb={8}
      >
        Map issue statuses to exclude from each metric's calculation
      </Text>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <Stack spacing={4}>
              {METRIC_STATUS_EXCLUSION_METRICS.map(({ key, label }) => (
                <HStack spacing={4} key={key} align="flex-start">
                  <Text
                    fontSize="sm"
                    fontFamily="heading"
                    fontWeight="semibold"
                    color="text.secondary"
                    lineHeight="17px"
                    width="160px"
                    pt={2}
                  >
                    {label}:
                  </Text>
                  <Box flex={1}>
                    <Field
                      as={Select}
                      name={`exclusions.${key}`}
                      placeholder="Select statuses to exclude"
                      isMulti
                      useBasicStyles
                      closeMenuOnSelect={false}
                      menuPlacement="auto"
                      onChange={(selectedOption: { value: string }[]) => {
                        setFieldValue(
                          `exclusions.${key}`,
                          selectedOption.map((item) => item.value)
                        );
                      }}
                      options={statusOptions}
                      value={(values.exclusions[key] ?? []).map((status) => ({
                        value: status,
                        label: status,
                      }))}
                      isDisabled={isManager}
                    />
                  </Box>
                </HStack>
              ))}
              <Box mt={6}>
                <SaveAndPreviousButton
                  onSubmit={() => onSubmit(values)}
                  isDisabled={isManager}
                />
              </Box>
            </Stack>
          </Form>
        )}
      </Formik>
      {(exclusionLoading ||
        issueStatusLoading ||
        saveMetricStatusExclusionLoading) && <Loader />}
    </>
  );
};

export default MetricStatusExclusion;
